import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { SettingModelSettingType } from 'api/models';
import { useGetElementTypesBySettingType } from 'api/queries/poi/poi';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { DimensionElementTypes } from 'app/modules/wallforming/components/scenes/objects/dimensions/core/dimensionScene';

const CACHE_TIME = 24 * 60 * 60 * 1000;

export function useGetDimensionElementTypes(): DimensionElementTypes {
    const { t } = useTranslation();

    const isVisible = usePlacementStore(useShallow((state) => state.dimensionSettings.visible));

    const [selectedPoiFilterType, setSelectedPoiFilterType] = useState<POIFilterType | undefined>(
        usePlacementStore.getState().poiSelection.current.filterType
    );

    const isEnabled = useMemo(
        () => isVisible && selectedPoiFilterType === POIFilterType.Wall,
        [isVisible, selectedPoiFilterType]
    );

    useEffect(() => {
        const subscribedPoiFilterType = usePlacementStore.subscribe(
            (state) => state.poiSelection.current.filterType,
            (newFilterType) => {
                setSelectedPoiFilterType(newFilterType);
            }
        );

        return () => subscribedPoiFilterType();
    }, []);

    const panelsQuery = useGetElementTypesBySettingType(SettingModelSettingType.Panels, {
        query: {
            queryKey: [SettingModelSettingType.Panels],
            enabled: isEnabled,
            staleTime: CACHE_TIME,
            gcTime: CACHE_TIME
        }
    });
    const cornersQuery = useGetElementTypesBySettingType(SettingModelSettingType.Corners, {
        query: {
            queryKey: [SettingModelSettingType.Corners],
            enabled: isEnabled,
            staleTime: CACHE_TIME,
            gcTime: CACHE_TIME
        }
    });
    const stopEndQuery = useGetElementTypesBySettingType(SettingModelSettingType.StopEnd, {
        query: {
            queryKey: [SettingModelSettingType.StopEnd],
            enabled: isEnabled,
            staleTime: CACHE_TIME,
            gcTime: CACHE_TIME
        }
    });
    const connectorsQuery = useGetElementTypesBySettingType(SettingModelSettingType.Connectors, {
        query: {
            queryKey: [SettingModelSettingType.Connectors],
            enabled: isEnabled,
            staleTime: CACHE_TIME,
            gcTime: CACHE_TIME
        }
    });
    const anchorsQuery = useGetElementTypesBySettingType(SettingModelSettingType.Anchors, {
        query: {
            queryKey: [SettingModelSettingType.Anchors],
            enabled: isEnabled,
            staleTime: CACHE_TIME,
            gcTime: CACHE_TIME
        }
    });

    const error = useMemo(
        () =>
            panelsQuery.error ??
            cornersQuery.error ??
            stopEndQuery.error ??
            connectorsQuery.error ??
            anchorsQuery.error,
        [panelsQuery.error, cornersQuery.error, stopEndQuery.error, connectorsQuery.error, anchorsQuery.error]
    );

    useEffect(() => {
        if (isEnabled && error) {
            toastNotifications.error({ title: t('error'), message: t('errorDimensionElementTypes') });
        }
    }, [isEnabled, error, t]);

    const panels = panelsQuery.data?.data;
    const corners = cornersQuery.data?.data;
    const stopEnd = stopEndQuery.data?.data;
    const connectors = connectorsQuery.data?.data;
    const anchors = anchorsQuery.data?.data;

    return useMemo(() => {
        return {
            panels: new Set([...(panels ?? []), ...(corners ?? []), ...(stopEnd ?? [])]),
            connectors: new Set(connectors),
            anchors: new Set(anchors)
        };
    }, [panels, corners, stopEnd, connectors, anchors]);
}
